<template>
  <div class="card">
    <h2 class="mb-1 text-lg font-medium">Previous imports</h2>
    <p class="mb-3 text-xs text-gray-500">Reuse an earlier parse instead of re-uploading — matches reflect the portal data at parse time.</p>

    <div class="flex flex-col gap-4 lg:flex-row">
      <div class="lg:w-72 lg:shrink-0 lg:border-r lg:border-gray-100 lg:pr-4">
        <input v-model="search" type="search" class="input mb-2 w-full py-1 text-sm" placeholder="Search file, user or vendor" />
        <div class="mb-2 flex items-center justify-between gap-2">
          <button
            type="button"
            class="rounded-full border px-3 py-1 text-xs font-medium"
            :class="decreasesOnly ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'"
            :disabled="prefetching"
            @click="toggleDecreasesOnly"
          >
            {{ prefetching ? 'Checking…' : '⚠ Decreases' }}
          </button>
          <span v-if="unresolvedCount > 0 && decreasesOnly" class="text-xs text-gray-400">{{ unresolvedCount }} unchecked</span>
        </div>
        <ul class="m-0 max-h-[32rem] list-none space-y-1 overflow-y-auto p-0">
          <li v-for="record in visibleRecords" :key="record.id">
            <div class="rounded-lg border px-3 py-2" :class="record.id === selectedRecordId ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-gray-50'">
              <button type="button" class="block w-full text-left" @click="selectRecord(record.id)">
                <p class="m-0 truncate text-sm font-medium text-gray-900">{{ record.filename }}</p>
                <p class="m-0 text-xs text-gray-500">{{ formatDate(record.createdAt) }}</p>
                <p class="m-0 truncate text-xs text-gray-500">
                  {{ record.username }}
                  <template v-if="record.vendorName">· {{ record.vendorName }}</template>
                </p>
                <p class="m-0 text-xs">
                  <span v-if="record.createdCount === null" class="text-gray-400">not saved to the calendar</span>
                  <span v-else class="text-gray-500">
                    <span v-if="(decreaseCountFor(record.id) ?? 0) > 0" class="font-semibold text-red-700">⚠ {{ decreaseCountFor(record.id) }} decrease(s) ·</span>
                    {{ record.createdCount }} new, {{ record.updatedCount }} updated, {{ record.skippedCount }} kept
                  </span>
                </p>
                <p v-if="record.warningsCount > 0" class="m-0 text-xs text-amber-700">{{ record.warningsCount }} warning(s)</p>
              </button>
              <div class="mt-1 flex gap-3">
                <button type="button" class="text-xs font-medium text-blue-700 hover:underline" :disabled="store.loading" @click="emit('load', record.id)">Load</button>
                <button type="button" class="text-xs font-medium text-red-700 hover:underline" :disabled="deletingRecordId === record.id" @click="pendingDeleteRecord = record">
                  {{ deletingRecordId === record.id ? 'Deleting…' : 'Delete' }}
                </button>
              </div>
            </div>
          </li>
        </ul>
        <p v-if="visibleRecords.length === 0" class="m-0 py-6 text-center text-sm text-gray-500">No imports match.</p>
      </div>

      <div class="min-w-0 flex-1">
        <p v-if="selectedRecordId === null" class="py-10 text-center text-sm text-gray-500">Select an import to see what it changed.</p>
        <p v-else-if="detailLoading" class="py-10 text-center text-sm text-gray-500">Loading changes…</p>
        <div v-else-if="detailError" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {{ detailError }}
          <button type="button" class="ml-2 font-medium underline" @click="loadDetail(selectedRecordId)">Retry</button>
        </div>
        <ImportAppliedChanges v-else-if="appliedChanges" :changes="appliedChanges" />
        <div v-else-if="parsedPreview">
          <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p class="m-0 text-sm font-medium text-gray-700">{{ parsedPreviewHeading }}</p>
            <button type="button" class="text-xs font-medium text-blue-700 hover:underline" :disabled="store.loading" @click="emit('load', selectedRecordId)">
              Load this parse
            </button>
          </div>
          <ImportParsedPreview
            :preview="parsedPreview"
            :entries="calendar?.entries ?? null"
            :allocations="calendar?.allocations ?? null"
            :comparison-loading="calendarLoading"
            :comparison-error="calendarError"
          />
        </div>
        <p v-else class="py-10 text-center text-sm text-gray-500">This import changed nothing.</p>
      </div>
    </div>

    <ConfirmDialog
      :open="pendingDeleteRecord !== null"
      title="Delete import record"
      :message="`Delete the parse record for “${pendingDeleteRecord?.filename}”? Capacity data already saved from it is untouched. This cannot be undone.`"
      confirm-label="Delete"
      @cancel="pendingDeleteRecord = null"
      @confirm="onConfirmDeleteRecord"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import ImportAppliedChanges from '@/components/capacity/ImportAppliedChanges.vue';
  import ImportParsedPreview from '@/components/capacity/ImportParsedPreview.vue';
  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import { fetchCapacityCalendar } from '@/services/capacityCalendarService';
  import { fetchImportRecord } from '@/services/capacityImportService';
  import logger from '@/services/logger';
  import { useCapacityImportStore } from '@/store/capacityImportStore';
  import type { CapacityCalendarData } from '@/types/capacityCalendar';
  import type { AppliedImportChanges, CapacityImportRecordDetail, CapacityImportRecordSummary, CapacityPlanParseResult } from '@/types/capacityImport';
  import { extractErrorMessage } from '@/utils/apiError';
  import { summarize, toDiffRows } from '@/utils/importDiffRows';

  const emit = defineEmits<{ load: [recordId: string] }>();

  const store = useCapacityImportStore();
  const route = useRoute();
  const router = useRouter();

  const selectedRecordId = ref<string | null>(null);
  const detail = ref<CapacityImportRecordDetail | null>(null);
  const detailLoading = ref(false);
  const detailError = ref<string | null>(null);
  const pendingDeleteRecord = ref<CapacityImportRecordSummary | null>(null);
  const deletingRecordId = ref<string | null>(null);
  const search = ref('');
  const decreasesOnly = ref(false);
  const prefetching = ref(false);
  // Detail is the only place a decrease count exists — the record summary has no such field.
  // Cached so the Decreases filter pays for each record at most once per session.
  const detailCache = ref(new Map<string, CapacityImportRecordDetail>());
  const unresolvedCount = ref(0);
  // Calendar rows/allocations backing the parsed pane's comparison. The import page never loads
  // the calendar otherwise, so it's fetched on demand the first time a parsed preview needs it
  // and kept for the rest of the session — every record compares against the same snapshot.
  const calendar = ref<CapacityCalendarData | null>(null);
  const calendarLoading = ref(false);
  const calendarError = ref<string | null>(null);
  let calendarRequest: Promise<void> | null = null;

  // Narrowed to a non-null computed rather than asserted in the template — vue-tsc checks
  // template expressions, and `!` on a ref there is an ESLint fight for no benefit.
  const appliedChanges = computed<AppliedImportChanges | null>(() => {
    const changes = detail.value?.appliedChanges;
    return changes && changes.vendors.some((vendor) => vendor.changes.length > 0) ? changes : null;
  });

  // Same non-null-computed pattern as appliedChanges above, for the same vue-tsc reason —
  // detail.value?.parsedPreview would re-narrow poorly across the v-else-if chain in the template.
  const parsedPreview = computed<CapacityPlanParseResult | null>(() => detail.value?.parsedPreview ?? null);

  // A record with no diff to show is not necessarily unconfirmed — a confirm where every
  // in-scope vendor resolved to zero effective writes still persists a non-null appliedChanges
  // with an empty change set, so appliedChanges alone can't tell "never applied" from "applied,
  // changed nothing". createdCount is the reliable signal: it's null only when nothing was ever
  // confirmed. Read off `detail`, not a separate list lookup, so the heading always describes
  // the record whose preview is actually on screen.
  const parsedPreviewHeading = computed<string>(() =>
    detail.value?.createdCount === null ? 'Not saved to the calendar — showing the parsed file instead.' : 'This import changed nothing — showing the parsed file instead.'
  );

  // Deduped: concurrent selections share one request, and a success is never re-fetched. A
  // failure clears the latch so Retry-by-reselecting can try again.
  function ensureCalendar(): Promise<void> {
    if (calendar.value || calendarRequest) return calendarRequest ?? Promise.resolve();
    calendarLoading.value = true;
    calendarError.value = null;
    calendarRequest = fetchCapacityCalendar()
      .then((data) => {
        calendar.value = data;
      })
      .catch((fetchError) => {
        calendarRequest = null;
        calendarError.value = extractErrorMessage(fetchError, 'Could not load the calendar to compare against — showing the parsed values only');
      })
      .finally(() => {
        calendarLoading.value = false;
      });
    return calendarRequest;
  }

  // Only the parsed pane compares against the calendar, so nothing is fetched until one is shown.
  watch(parsedPreview, (preview) => {
    if (preview) void ensureCalendar();
  });

  function decreaseCountFor(recordId: string): number | null {
    const cached = detailCache.value.get(recordId);
    if (!cached?.appliedChanges) return null;
    return summarize(toDiffRows(cached.appliedChanges)).decreased;
  }

  const visibleRecords = computed<CapacityImportRecordSummary[]>(() => {
    const needle = search.value.trim().toLowerCase();
    return store.records.filter((record) => {
      const haystack = `${record.filename} ${record.username} ${record.vendorName ?? ''}`.toLowerCase();
      if (needle && !haystack.includes(needle)) return false;
      if (decreasesOnly.value && (decreaseCountFor(record.id) ?? 0) === 0) return false;
      return true;
    });
  });

  // A filter change (e.g. toggling Decreases on) can drop the selected record from the list
  // without touching selectedRecordId/detail — clear the stale selection so the pane never
  // shows a diff with no highlighted row to explain where it came from. Only reacts to
  // visibleRecords, so clearing the selection here (which doesn't affect search/decreasesOnly/
  // detailCache) cannot re-trigger this watcher.
  watch(visibleRecords, (records) => {
    if (selectedRecordId.value !== null && !records.some((record) => record.id === selectedRecordId.value)) {
      clearSelection();
    }
  });

  // Turning the filter on resolves every saved record's decrease count in one parallel pass.
  // GET /capacity-import/records returns at most 20 rows, so this is bounded, and it only ever
  // runs on an explicit click. A failed fetch is skipped and reported, not fatal.
  async function toggleDecreasesOnly(): Promise<void> {
    if (decreasesOnly.value) {
      decreasesOnly.value = false;
      return;
    }
    const missing = store.records.filter((record) => record.createdCount !== null && !detailCache.value.has(record.id));
    unresolvedCount.value = 0;
    if (missing.length > 0) {
      prefetching.value = true;
      const settled = await Promise.allSettled(missing.map((record) => fetchImportRecord(record.id)));
      settled.forEach((outcome) => {
        if (outcome.status === 'fulfilled') detailCache.value.set(outcome.value.id, outcome.value);
      });
      unresolvedCount.value = settled.filter((outcome) => outcome.status === 'rejected').length;
      prefetching.value = false;
    }
    decreasesOnly.value = true;
  }

  async function selectRecord(recordId: string): Promise<void> {
    selectedRecordId.value = recordId;
    void router.replace({ query: { ...route.query, changes: recordId } });
    await loadDetail(recordId);
  }

  async function loadDetail(recordId: string): Promise<void> {
    detailLoading.value = true;
    detailError.value = null;
    try {
      const fetched = await fetchImportRecord(recordId);
      detailCache.value.set(fetched.id, fetched);
      if (selectedRecordId.value !== recordId) return;
      detail.value = fetched;
    } catch (fetchError) {
      if (selectedRecordId.value !== recordId) return;
      detail.value = null;
      detailError.value = extractErrorMessage(fetchError, 'Failed to load the applied changes');
      logger.showError(detailError.value);
    } finally {
      if (selectedRecordId.value === recordId) detailLoading.value = false;
    }
  }

  function clearSelection(): void {
    selectedRecordId.value = null;
    detail.value = null;
    detailError.value = null;
    // An in-flight loadDetail only clears the flag for the still-selected record, so clearing the
    // selection mid-fetch would otherwise strand it true.
    detailLoading.value = false;
    const query = { ...route.query };
    delete query.changes;
    void router.replace({ query });
  }

  async function onConfirmDeleteRecord(): Promise<void> {
    const record = pendingDeleteRecord.value;
    if (!record) return;
    pendingDeleteRecord.value = null;
    deletingRecordId.value = record.id;
    await store.deleteRecord(record.id);
    if (selectedRecordId.value === record.id) clearSelection();
    deletingRecordId.value = null;
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }

  // The parent gates this panel on store.records being populated, so by the time it mounts
  // the deep-linked id can be checked against a real record instead of 404-ing on a stale link.
  onMounted(() => {
    const requested = route.query.changes;
    if (typeof requested !== 'string') return;
    const record = store.records.find((candidate) => candidate.id === requested);
    if (record) void selectRecord(requested);
  });
</script>
